export const maskCEP = (value?: string | number | null): string => {
  const str = String(value ?? '');
  // Remove tudo que não for número e limita a 8 caracteres
  const digits = str.replace(/\D/g, '').slice(0, 8);
  
  // Aplica a máscara XXXXX-XXX progressivamente conforme a digitação
  return digits.replace(/^(\d{5})(\d)/, '$1-$2');
};

export const maskPhone = (value?: string | number | null): string => {
  const str = String(value ?? '');
  // Remove tudo que não for número e limita a 11 caracteres no máximo
  const digits = str.replace(/\D/g, '').slice(0, 11);

  // Aplica a formatação progressivamente de acordo com o tamanho
  if (digits.length <= 2) {
    return digits.replace(/^(\d{1,2})/, '($1');
  } else if (digits.length <= 6) {
    return digits.replace(/^(\d{2})(\d{1,4})/, '($1) $2');
  } else if (digits.length <= 10) { 
    // Formato fixo: (XX) XXXX-XXXX
    return digits.replace(/^(\d{2})(\d{4})(\d{1,4})/, '($1) $2-$3');
  } else { 
    // Formato celular (11 dígitos): (XX) 9XXXX-XXXX
    return digits.replace(/^(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
};

export const onlyNumbers = (value?: string | number | null): string => {
  if (value === undefined || value === null || value === '') return '';
  return String(value).replace(/\D/g, '');
};

export const capitalizeName = (name?: string | null): string => {
  if (!name || typeof name !== 'string') return '';
  
  return name
    .toLowerCase()
    .split(' ')
    .filter(word => word.length > 0)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const formatDocument = (value?: string | number | null): string => {
  if (value === undefined || value === null || value === '') return "-";
  const str = String(value);
  const digits = str.replace(/\D/g, "");
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  }
  if (digits.length === 14) {
    return digits.replace(
      /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
      "$1.$2.$3/$4-$5"
    );
  }
  return str;
};

export const maskCpfCnpj = (value?: string | number | null): string => {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (digits.length <= 11) {
    return digits
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2")
      .substring(0, 14);
  }
  return digits
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2")
    .substring(0, 18);
};

export const formatPhone = (value?: string | number | null): string => {
  if (value === undefined || value === null || value === "") return "-";
  const str = String(value);
  let digits = str.replace(/\D/g, "");

  if (
    (digits.length === 12 || digits.length === 13) &&
    digits.startsWith("55")
  ) {
    digits = digits.slice(2);
  }

  if (digits.length === 11) {
    return digits.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
  }
  if (digits.length === 10) {
    return digits.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
  }
  if (digits.length === 9) {
    return digits.replace(/(\d{5})(\d{4})/, "$1-$2");
  }
  if (digits.length === 8) {
    return digits.replace(/(\d{4})(\d{4})/, "$1-$2");
  }

  return str;
};

export const formatCep = (value?: string | number | null): string => {
  if (value === undefined || value === null || value === "") return "";
  const digits = String(value).replace(/\D/g, "");
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5, 8)}`;
};

export const isValidCPF = (value?: string | number | null): boolean => {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (digits.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(digits)) return false;

  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += Number(digits[i]) * (10 - i);
  }
  let remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== Number(digits[9])) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += Number(digits[i]) * (11 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  return remainder === Number(digits[10]);
};

export const isValidCNPJ = (value?: string | number | null): boolean => {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (digits.length !== 14) return false;
  if (/^(\d)\1{13}$/.test(digits)) return false;

  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += Number(digits[i]) * weights1[i];
  }
  let remainder = sum % 11;
  const digit1 = remainder < 2 ? 0 : 11 - remainder;
  if (digit1 !== Number(digits[12])) return false;

  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  sum = 0;
  for (let i = 0; i < 13; i++) {
    sum += Number(digits[i]) * weights2[i];
  }
  remainder = sum % 11;
  const digit2 = remainder < 2 ? 0 : 11 - remainder;
  return digit2 === Number(digits[13]);
};

export interface ILicenseeNameLike {
  name?: string | null;
  company_name?: string | null;
  person_type?: string | null;
  is_person?: boolean | null;
}

export const getLicenseeDisplayName = (
  licensee?: ILicenseeNameLike | null,
  fallback = ""
): string => {
  if (!licensee) return fallback;
  const isPf =
    licensee.is_person === true ||
    (Boolean(licensee.person_type) &&
      String(licensee.person_type).toUpperCase() === "FISICA");
  if (isPf) {
    return (licensee.name || "").trim() || fallback;
  }
  return (licensee.company_name || licensee.name || "").trim() || fallback;
};
